import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { rupee, imageUrl } from "../lib/api";
import { VegBadge } from "./VegBadge";
import { Plus, Star } from "lucide-react";
import { toast } from "sonner";

export function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const hasVariants = product.variants && product.variants.length > 0;
  const [selected, setSelected] = useState(0);

  const activeVariant = hasVariants ? product.variants[selected] : null;
  const price = hasVariants ? activeVariant.price : product.base_price;

  const pickVariant = (e, i) => {
    e.preventDefault();
    e.stopPropagation();
    setSelected(i);
  };

  const quickAdd = (e) => {
    e.preventDefault();
    addItem({
      product_id: product.id, slug: product.slug, name: product.name, image: product.image,
      variant: hasVariants ? activeVariant.name : null, unit_price: price,
      customizations: [], add_ons: [], quantity: 1, veg: product.veg,
    });
    toast.success(`${product.name}${hasVariants ? ` (${activeVariant.name})` : ""} added to cart`);
  };

  return (
    <Link
      to={`/product/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="group card-hover bg-white rounded-xl border border-black/5 overflow-hidden flex flex-col fade-up"
      style={{ animationDelay: `${(index % 8) * 45}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-beige">
        <img src={imageUrl(product.image)} alt={product.name} loading="lazy"
             className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute top-3 left-3 flex gap-2">
          {product.best_seller && (
            <span className="flex items-center gap-1 bg-navy text-gold text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full">
              <Star size={10} className="fill-gold" /> Best Seller
            </span>
          )}
          {product.new_item && (
            <span className="bg-gold text-navy text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full">New</span>
          )}
        </div>
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded p-1"><VegBadge veg={product.veg} /></div>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-serif text-lg leading-tight text-navy line-clamp-2">{product.name}</h3>
        <p className="text-xs text-muted-foreground mt-1 line-clamp-2 flex-1">{product.short_description}</p>

        {hasVariants && (
          <div className="mt-3" data-testid={`variants-${product.slug}`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Select Size / Portion</div>
            <div className="flex flex-wrap gap-1.5">
              {product.variants.map((v, i) => (
                <button
                  key={v.name + i}
                  onClick={(e) => pickVariant(e, i)}
                  data-testid={`variant-${product.slug}-${i}`}
                  aria-pressed={selected === i}
                  className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${selected === i ? "bg-navy text-cream border-navy" : "bg-white text-navy border-black/15 hover:border-gold"}`}
                >
                  {v.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mt-3 pt-3 border-t border-dashed border-black/10">
          <div>
            <span className="font-semibold text-navy" data-testid={`price-${product.slug}`}>{rupee(price)}</span>
          </div>
          <button
            onClick={quickAdd}
            data-testid={`quick-add-${product.slug}`}
            className="w-full sm:w-auto flex items-center justify-center gap-1 bg-navy text-cream text-xs font-semibold px-3 py-2 rounded-full hover:bg-gold hover:text-navy transition-colors active:scale-95"
          >
            <Plus size={14} /> Add
          </button>
        </div>
      </div>
    </Link>
  );
}
